import type { ClassDefinition } from '../../schemas/classDefinition';
import { guerrieroAdvanced2to5Moves } from '../moves/guerriero/advanced-2-5';
import { guerrieroAdvanced6to10Moves } from '../moves/guerriero/advanced-6-10';
import {
  guerrieroStartingAutoMoves,
  guerrieroStartingChoiceMoves,
} from '../moves/guerriero/initial';

/**
 * Definizione della classe **Guerriero** (manuale V1, rr. 3319-3667).
 *
 * Seconda classe trascritta. Pattern principali stress-testati:
 * - **D1 (FI-19 risolto)**: `EquipmentChoiceGroup.pickCount: 2` per il
 *   gruppo "scegli 2 di 4" dell'equipaggiamento extra (manuale r. 3373)
 * - **D3 (FI-19 risolto)**: `prerequisites` con `anyOf` per le mosse con OR
 *   (Combattente Versatile, Cimelio, Arma Migliorata, Fabbro) inclusi
 *   prerequisiti **cross-class** verso `warlock.vincolo-della-lama`
 * - **D2 (FI-18 rimandato)**: *Arma Prescelta* ha 3 sotto-scelte indipendenti
 *   (tipologia, portata, miglioramenti) che vivono nella description
 *
 * **Punti notevoli**:
 * - Dado di danno **d10**, PF base 10, Carico base 12
 * - 5 archetipi razza-tematici standard
 * - 3 mosse iniziali automatiche + 2 mosse iniziali di scelta (`pickCount: 1`)
 * - 16 mosse avanzate L2-5, 14 mosse avanzate L6-10 (40 mosse totali)
 * - 4 template di Legami (= `maxBonds`, refine FI-3 rispettato)
 * - **Non casteggiante**: `spellcasting` resta `undefined`
 * - **22 monete** in opzione equipaggiamento (Item con `quantity: 22`,
 *   FI-16 risolto) + *Arma Prescelta* in `startingEquipment.always`
 */
export const guerrieroClassDefinition: ClassDefinition = {
  id: 'guerriero',
  name: 'Guerriero',

  intro:
    "È un lavoro sporco. Armatura e muscoli, giorno dopo giorno, buttato nella mischia come carne da macello. Non aspettarti fanfare per quella lama che ti ha quasi aperto in due a Bucksberg. Nessuno canterà le tue gesta, di quando li hai tirati fuori a forza dalle Fosse della Follia, con le budella che gli si attorcigliavano per la paura. Nessuno se ne ricorderà.\n\nMa a te non importa. Tu lo fai per la scarica di adrenalina che ti fa ribollire il sangue nelle vene quando sei nel mezzo alla battaglia. Vincere o morire. E il solo fatto di essere qui a raccontarlo dimostra di che pasta sei fatto! Sei un muro di carne e metallo. Lascia che ogni pericolo si infranga contro di te, riducendosi in polvere. I tuoi compagni, attorno al fuoco, si lamentano delle loro ferite. Tu, invece, porti le tue cicatrici come trofei, e per te ogni ammaccatura, ogni crepa nella tua armatura, è una storia da raccontare.\n\nQuando stringi la *tua arma* la paura scompare. Te ne prendi cura. Con lei hai instaurato un rapporto speciale: è la tua amica, la tua compagna, la tua confidente… Forse è lo spirito che vi dimora, o magari solo i troppi dannati anni trascorsi dalla prima volta che l'hai brandita. Anche gli altri impugnano lame d'acciaio, ma tu, Guerriero, tu *sei* acciaio. Acciaio temprato da infinite battaglie, e la battaglia è il tuo destino. Forse sei un soldato di lungo corso, o magari un astuto mercenario. O forse, ancora, un elegante spadaccino. La tua forza non risiede solo nei muscoli, ma anche nel tuo intuito e nell'esperienza di chi ha giocato a dadi con la Morte così tante volte da perderne il conto. Comunque vada, lo sai, alla fine sarai l'unico a restare in piedi, sotto il cielo implacabile.",

  // Manuale V1, rr. 3330-3338
  suggestedNames: [
    // Umano
    'Hawke',
    'Rudiger',
    'Gregor',
    'Berta',
    'Walton',
    'Castor',
    'Shanna',
    'Ajax',
    'Hob',
    // Elfo
    'Elohiir',
    'Sharaseth',
    'Hasrith',
    'Shevaral',
    'Cadeus',
    'Eldar',
    'Kithracet',
    'Thelian',
    // Nano
    'Grug',
    'Surtur',
    'Brunhilda',
    'Annika',
    'Janos',
    'Greta',
    'Dim',
    'Rundrig',
    'Jarl',
    'Xotoq',
    // Halfling
    'Finnegan',
    'Olive',
    'Randolph',
    'Bartleby',
    'Aubrey',
    'Baldwin',
    'Becca',
    'Orso',
    // Mezz'orco
    'Grimharg',
    'Glukda',
    'Meggri',
    'Ska',
    'Luknosh',
    'Draglurzka',
    'Bolsha',
    "Mor'du",
  ],

  // Niente sezione "Titoli" per il Guerriero (è specifica del Barbaro)
  suggestedTitles: [],

  // Manuale V1, rr. 3340-3346
  aspetti: [
    {
      label: 'Occhi',
      options: ['Duri', 'Spenti', 'Bramosi'],
    },
    {
      label: 'Capelli',
      options: ['Selvaggi', 'Rasati', 'Elmo ammaccato'],
    },
    {
      label: 'Pelle',
      options: ['Callosa', 'Abbronzata', 'Piena di cicatrici'],
    },
    {
      label: 'Corpo',
      options: ['Atletico', 'Snello', 'Martoriato'],
    },
  ],

  // Manuale V1, rr. 3348-3354
  damageDie: 'd10',
  baseHp: 10,
  baseLoad: 12,

  // Manuale V1, rr. 3356-3362
  alignmentPrecepts: {
    buono: 'Difendi chi è più debole di te.',
    legale:
      'Ti metti in pericolo per portare diligentemente a termine un compito che ti è stato affidato.',
    neutrale: 'Sconfiggi personalmente un degno avversario.',
    caotico: "Crei il caos per avere un'opportunità di essere assoldato.",
    malvagio: 'Uccidi un nemico indifeso o che si sia arreso.',
  },

  // Manuale V1, rr. 3383-3389. maxBonds = 4 (FI-3: deve coincidere con la lunghezza)
  bondTemplates: [
    '_____ mi deve la vita, che voglia ammetterlo o no.',
    'Ho giurato di proteggere _____.',
    '_____ è più forte di me, e questo non posso accettarlo.',
    "Io e _____ formeremo un muro contro cui il nemico s'infrangerà.",
  ],
  maxBonds: 4,

  // Manuale V1, rr. 3391-3397
  archetypes: [
    {
      id: 'umano',
      name: 'Umano',
      associatedRace: 'umano',
      description:
        'Quando *discerni la realtà* durante una battaglia, aggiungi una domanda sulla prossima mossa di un personaggio.',
      grantedMoveId: 'guerriero.archetipo-umano',
    },
    {
      id: 'elfo',
      name: 'Elfo',
      associatedRace: 'elfo',
      description:
        "Quando attacchi con un'arma a una mano (coltello/pugnale/spada), considera che abbia *precisa*.",
      grantedMoveId: 'guerriero.archetipo-elfo',
    },
    {
      id: 'nano',
      name: 'Nano',
      associatedRace: 'nano',
      description: 'Quando bevi insieme a qualcuno, puoi *parlamentare* tirando+**COS**.',
      grantedMoveId: 'guerriero.archetipo-nano',
    },
    {
      id: 'halfling',
      name: 'Halfling',
      associatedRace: 'halfling',
      description: 'Una volta per combattimento, puoi ritirare un singolo tiro per i danni.',
      grantedMoveId: 'guerriero.archetipo-halfling',
    },
    {
      id: 'mezzorco',
      name: "Mezz'orco",
      associatedRace: 'mezzorco',
      description:
        'Quando incontri insegne/tattiche/storie militari, puoi fare una domanda al GM che ti risponderà sinceramente.',
      grantedMoveId: 'guerriero.archetipo-mezzorco',
    },
  ],

  startingMoves: guerrieroStartingAutoMoves.map((m) => m.id),

  startingChoiceGroups: [
    {
      id: 'mossa-iniziale-aggiuntiva',
      name: 'Mossa iniziale aggiuntiva',
      description: 'Scegli una di queste due mosse oltre alle 3 iniziali automatiche.',
      moveOptions: guerrieroStartingChoiceMoves.map((m) => m.id),
      pickCount: 1,
      expandableInAdvanced: false,
    },
  ],

  advancedMoves2to5: guerrieroAdvanced2to5Moves.map((m) => m.id),
  advancedMoves6to10: guerrieroAdvanced6to10Moves.map((m) => m.id),

  // Il Guerriero non è casteggiante: spellcasting resta undefined.

  // Manuale V1, rr. 3365-3381
  startingEquipment: {
    always: [
      // Arma Prescelta: la configurazione (tipologia/portata/miglioramenti)
      // viene fatta tramite la mossa starting omonima. Item placeholder.
      {
        id: 'arma-prescelta',
        name: 'Arma Prescelta',
        weight: 2,
        tags: [],
        description:
          'Configurazione tramite la mossa *Arma Prescelta*: scegli tipologia (1 di 7), portata (1 di 3) e 2 miglioramenti (di 10). Vedi la mossa per i dettagli.',
      },
      {
        id: 'razioni-da-dungeon',
        name: 'Razioni da Dungeon',
        weight: 1,
        uses: 5,
        tags: ['razione'],
      },
    ],
    choices: [
      {
        id: 'armatura-iniziale',
        prompt: 'Scegli la tua armatura',
        pickCount: 1,
        options: [
          {
            id: 'maglia-piu-attrezzatura',
            label: 'Armatura di maglia + Attrezzatura da Avventuriero',
            items: [
              {
                id: 'armatura-di-maglia',
                name: 'Armatura di maglia',
                weight: 1,
                armor: 1,
                tags: ['indossato'],
              },
              {
                id: 'attrezzatura-da-avventuriero',
                name: 'Attrezzatura da Avventuriero',
                weight: 2,
                uses: 5,
                tags: [],
                description:
                  'Da questo mucchio informe di roba salterà sempre fuori qualcosa di utile.',
              },
            ],
          },
          {
            id: 'armatura-a-scaglie',
            label: 'Armatura a scaglie',
            items: [
              {
                id: 'armatura-a-scaglie',
                name: 'Armatura a scaglie',
                weight: 3,
                armor: 2,
                tags: ['indossato', 'ingombrante'],
              },
            ],
          },
        ],
      },
      {
        id: 'extra-iniziale',
        prompt: 'Scegli due opzioni tra le seguenti',
        // FI-19 risolto: pickCount: 2 per "scegli 2 di 4" (manuale r. 3373)
        pickCount: 2,
        options: [
          {
            id: 'due-pozioni-curative',
            label: '2 Pozioni Curative',
            items: [
              {
                id: 'pozioni-curative',
                name: 'Pozioni Curative',
                weight: 0,
                quantity: 2,
                uses: 1,
                tags: [],
                description: 'Recupera PF se bevuta.',
              },
            ],
          },
          {
            id: 'scudo',
            label: 'Scudo',
            items: [
              {
                id: 'scudo',
                name: 'Scudo',
                weight: 2,
                armor: 1,
                tags: [],
                description: 'Quando usato.',
              },
            ],
          },
          {
            id: 'monete-x22',
            label: '22 monete',
            items: [
              {
                id: 'monete',
                name: 'Monete',
                weight: 0,
                quantity: 22,
                tags: [],
                description: 'Soldi del PG.',
              },
            ],
          },
          {
            id: 'antitossina-razioni-erbe',
            label: 'Antitossina + Razioni + Erbe e Unguenti',
            items: [
              {
                id: 'antitossina',
                name: 'Antitossina',
                weight: 0,
                uses: 1,
                tags: [],
              },
              {
                id: 'razioni-da-dungeon-extra',
                name: 'Razioni da Dungeon (extra)',
                weight: 1,
                uses: 5,
                tags: ['razione'],
              },
              {
                id: 'erbe-e-unguenti',
                name: 'Erbe e Unguenti',
                weight: 1,
                uses: 2,
                tags: ['lento'],
              },
            ],
          },
        ],
      },
    ],
  },
};
